import * as React from 'react'

import { Temporal } from '@js-temporal/polyfill'

import {
  Button,
  ChevronLeftIcon,
  DateTimePicker,
  Flex,
  Separator,
  Tooltip,
  useControllableState,
} from '@buffer-mono/popcorn'
import type { TimeSuggestion } from '@buffer-mono/popcorn/src/components/TimeInput/TimeInput'

import type { Timeslot } from '../../../stores/types'

import styles from './ScheduleDatePicker.module.css'
// todo use type from hook

interface ScheduleDatePickerProps {
  value?: Temporal.ZonedDateTime
  defaultValue: Temporal.ZonedDateTime
  onSubmit: (value: Temporal.ZonedDateTime) => void
  onReset?: () => void
  onChange?: (value: Temporal.ZonedDateTime | undefined) => void
  timezone: string
  weekStartsOn: 0 | 1
  /**
   * Whether to use 12 or 24 hour format
   * @default 12
   */
  hourCycle?: 12 | 24
  slots?: ReadonlyArray<Timeslot>
  slotsLoading?: boolean
  channelIdForSlots?: string
}

const isSameDayAndTime = (
  dateTime1: Temporal.ZonedDateTime,
  dateTime2: Temporal.ZonedDateTime,
): boolean => {
  return (
    dateTime1.day === dateTime2.day &&
    dateTime1.month === dateTime2.month &&
    dateTime1.year === dateTime2.year &&
    dateTime1.hour === dateTime2.hour &&
    dateTime1.minute === dateTime2.minute
  )
}

export const ScheduleDatePicker: React.FC<ScheduleDatePickerProps> = ({
  value: controlledValue,
  defaultValue,
  onSubmit,
  onReset,
  onChange,
  timezone,
  weekStartsOn,
  hourCycle = 12,
  slots,
  slotsLoading,
}) => {
  const dirtyTimeRef = React.useRef(controlledValue !== undefined)
  const [value, setValue] = useControllableState<
    Temporal.ZonedDateTime | undefined
  >({
    prop: controlledValue,
    defaultProp: defaultValue,
    onChange,
  })

  const now = Temporal.Now.zonedDateTimeISO(timezone)
  // todo: consider a offset of seconds to not show slots that are close to now
  const nowEpochSeconds = now.epochSeconds

  const handleApply = (): void => {
    if (value) {
      onSubmit(value)
    }
  }

  const { suggestions, firstSuggestion } = React.useMemo(() => {
    if (!slots)
      return {
        suggestions: { data: [], loading: !!slotsLoading, label: 'Slots' },
        firstSuggestion: undefined,
      }

    const suggestions: Array<
      TimeSuggestion & { dateTime: Temporal.ZonedDateTime }
    > = slots
      .filter((slot) => {
        return slot.timestamp > nowEpochSeconds
      })
      .map((slot) => {
        const zonedDateTime = Temporal.Instant.fromEpochSeconds(
          slot.timestamp,
        ).toZonedDateTimeISO(timezone)

        const isDisabled = !slot.isSlotFree
        const isCurrentPostDate =
          isDisabled && value && isSameDayAndTime(zonedDateTime, value)

        return {
          dateTime: zonedDateTime,
          time: zonedDateTime.toPlainTime(),
          // do not disable the slot of the current selected time
          isDisabled: isDisabled && !isCurrentPostDate,
          isCurrentPostDate,
        }
      })

    const firstSuggestion = suggestions.find(
      (suggestion) => !suggestion.isDisabled,
    )

    return {
      suggestions: {
        data: suggestions,
        loading: !!slotsLoading,
        label: 'Slots',
      },
      firstSuggestion,
    }
  }, [slots, timezone, nowEpochSeconds, slotsLoading, value])

  const shouldUseFirstSlot =
    !suggestions.loading &&
    firstSuggestion &&
    !dirtyTimeRef.current &&
    value &&
    !firstSuggestion?.time.equals(value.toPlainTime())

  React.useEffect(() => {
    if (shouldUseFirstSlot && firstSuggestion) {
      setValue(firstSuggestion.dateTime)
    }
  }, [shouldUseFirstSlot, firstSuggestion, timezone, setValue])

  const onTimeChange = (time: Temporal.PlainTime | null): void => {
    if (time) {
      dirtyTimeRef.current = true
    }
  }

  return (
    <Flex direction="column" className={styles.scheduleDatePickerContainer}>
      <DateTimePicker
        value={value}
        onValueChange={setValue}
        onTimeChange={onTimeChange}
        timezone={timezone}
        weekStartsOn={weekStartsOn}
        min={now}
        hourCycle={hourCycle}
        timeSuggestions={suggestions}
        required={true}
      />
      <Separator className={styles.separator} />
      <Flex justify="between" align="center" className={styles.footer} gap="sm">
        {onReset && (
          <Tooltip content="Back to scheduling options">
            <Button variant="tertiary" onClick={onReset}>
              <ChevronLeftIcon /> More Scheduling Options
            </Button>
          </Tooltip>
        )}
        <Button variant="primary" onClick={handleApply} disabled={!value}>
          Apply
        </Button>
      </Flex>
    </Flex>
  )
}
